import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { LayoutGrid, ChevronLeft, Maximize2, Settings2, ChevronDown, ChevronUp, Building2, Loader2 } from 'lucide-react';
import { useAxesStore } from '../store/useAxesStore';
import SplitScreenView from '../components/axes/SplitScreenView';
import AxesBriefing from '../components/axes/AxesBriefing';

const BriefingHeader: React.FC = () => {
    const { isSearching, matchFound, searchError } = useAxesStore();
    return (
        <div className="flex items-center justify-between px-6 py-3 border-b border-white/5 bg-zinc-900/50">
            <div className="flex items-center gap-2">
                <Settings2 size={13} className="text-indigo-400" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Site & Briefing</span>
                {matchFound && (
                    <span className="text-[9px] bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-medium">
                        ✓ Found
                    </span>
                )}
                {searchError && (
                    <span className="text-[9px] bg-red-500/10 border border-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-medium" title={searchError}>
                        ✕ No match
                    </span>
                )}
            </div>
            <FindLayoutButton />
        </div>
    );
};

const FindLayoutButton: React.FC = () => {
    const { isSearching } = useAxesStore();
    // We dispatch a custom event so AxesBriefing's findLayout can handle it
    const handleClick = () => {
        window.dispatchEvent(new CustomEvent('axes:findLayout'));
    };
    return (
        <button
            onClick={handleClick}
            disabled={isSearching}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-[10px] font-bold transition-all shadow-md shadow-indigo-900/30"
        >
            {isSearching
                ? <><Loader2 size={11} className="animate-spin" /> Finding…</>
                : <><Building2 size={11} /> Find My Layout</>
            }
        </button>
    );
};

const AxesExplorer: React.FC = () => {
    const { activeLayout } = useAxesStore();

    if (activeLayout) {
        return (
            <div className="absolute inset-4 top-4 pointer-events-auto flex flex-col gap-4">
                {/* Main View Area - Now using SplitScreenView */}
                <div className="flex-1 rounded-3xl border border-white/5 overflow-hidden relative shadow-2xl shadow-black/50">
                    <SplitScreenView />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full gap-0 overflow-hidden bg-black relative">
            {/* Blank Canvas Area */}
            <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gradient-to-br from-zinc-900/50 to-black">
                <div className="text-center">
                    <div className="w-20 h-20 bg-indigo-500/10 rounded-full border border-indigo-500/20 flex flex-col items-center justify-center mx-auto mb-6 shadow-2xl shadow-indigo-500/10">
                        <Building2 size={32} className="text-indigo-400" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent mb-3 tracking-tight">
                        Precision Floorplan Engine
                    </h2>
                    <p className="text-sm text-zinc-500 max-w-sm mx-auto leading-relaxed">
                        Configure your lot dimensions, setbacks, and room requirements below. We'll instantly map the optimal layout to your exact plot.
                    </p>
                </div>
            </div>

            {/* Briefing section — visible before layout generation */}
            <div className="border-t border-white/5 shrink-0 bg-[#0a0a0a]">
                <BriefingHeader />
                <div className="max-h-[50vh] overflow-y-auto custom-scrollbar">
                    <AxesBriefing />
                </div>
            </div>
        </div>
    );
};

export default AxesExplorer;